import React, { useState, useEffect, useRef } from 'react';
import { GoogleGenerativeAI, HarmCategory, HarmBlockThreshold } from "https://cdn.jsdelivr.net/npm/@google/generative-ai@latest/+esm";
import robo from '../../assets/robo.png';
import beepSound from './../../assets/beep.mp3';
import { Mic, MicOff, Send, X, Volume2, VolumeX } from 'lucide-react';

const API_KEY = "AIzaSyB95re0HjI93gC5VwQhi2pnuYbK6JV_SpU";
const genAI = new GoogleGenerativeAI(API_KEY);

const username = "P";
const account_num = "9826112520";

const ENDPOINTS = {
  PACKAGES: "https://nodejs-serverless-function-express-henna-theta.vercel.app/api/package/all",
};

const safetySettings = [
  {
    category: HarmCategory.HARM_CATEGORY_HARASSMENT,
    threshold: HarmBlockThreshold.BLOCK_ONLY_HIGH,
  },
  {
    category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
    threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
  },
];

// Helper function to parse and format AI responses
const formatAIResponse = (text) => {
  // Handle asterisks and question formatting
  let formattedText = text;
  
  // Replace ***Question?** pattern with styled questions
  formattedText = formattedText.replace(/\*\*\*(.*?)\*\*/g, '<div class="font-semibold text-blue-700 mt-2">$1</div>');
  
  // Replace remaining ** with bolding
  formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Replace * with emphasis
  formattedText = formattedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Add paragraph breaks for better readability
  formattedText = formattedText.replace(/\n\n/g, '<div class="my-2"></div>');
  
  return formattedText;
};

const Chatbot = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [isVoiceSpeaking, setIsVoiceSpeaking] = useState(false);
  
  const messagesEndRef = useRef(null);
  const speechRecognitionRef = useRef(null);
  const speechSynthesisRef = useRef(null);
  const silenceTimerRef = useRef(null);
  
  // Initialize speech recognition and synthesis
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Speech Recognition Setup
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        speechRecognitionRef.current = new SpeechRecognition();
        speechRecognitionRef.current.continuous = true;
        speechRecognitionRef.current.interimResults = true;
        
        speechRecognitionRef.current.onresult = (event) => {
          const transcript = Array.from(event.results)
            .map(result => result[0])
            .map(result => result.transcript)
            .join('');
            
          setInput(transcript);
          
          // Reset silence timer on speech detection
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
          }
          
          // Set new silence timer
          silenceTimerRef.current = setTimeout(() => {
            if (transcript.trim() !== '') {
              handleVoiceSubmit(transcript);
            }
          }, 5000); // 5 seconds of silence
        };
        
        speechRecognitionRef.current.onerror = (event) => {
          console.error('Speech recognition error', event.error);
          setIsListening(false);
        };
      }
      
      // Speech Synthesis Setup
      speechSynthesisRef.current = window.speechSynthesis;
    }
    
    return () => {
      if (silenceTimerRef.current) {
        clearTimeout(silenceTimerRef.current);
      }
      stopListening();
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
    if (isOpen && messages.length === 0) {
      const welcomeMessage = "Hi! I'm your Trekking AI Assistant. How can I help you plan your perfect trek today?";
      setMessages([{ text: welcomeMessage, user: false, html: false }]);
      
      if (voiceEnabled) {
        speakText(welcomeMessage);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const playBeep = () => {
    const audio = new Audio(beepSound);
    audio.play();
  };

  const addMessage = (text, isUser, isHtml = false) => {
    setMessages(prev => [...prev, { text, user: isUser, html: isHtml }]);
    
    if (!isUser && voiceEnabled && text !== "Generating response...") {
      // For voice, we need plain text without HTML
      const plainText = isHtml ? text.replace(/<[^>]*>?/gm, '') : text;
      speakText(plainText);
    }
  };

  const fetchData = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch ${url}`);
    return response.json();
  };

  const generateContent = async (inputText) => {
    if (!inputText.trim()) return;

    addMessage(inputText, true);
    setInput('');
    setIsLoading(true);

    try {
      const packageDetails = await fetchData(ENDPOINTS.PACKAGES);

      const model = genAI.getGenerativeModel({
        model: "gemini-1.5-flash",
        systemInstruction: {
          role: "system",
          parts: [
            {
              text: `
User: @${username}
Account: ${account_num}
Available Trekking Packages: ${JSON.stringify(packageDetails)}

INSTRUCTIONS FOR RESPONSE FORMATTING:
1. Present questions in a clean, structured format
2. Don't use asterisks or markdown in your responses
3. Keep responses conversational and friendly
4. For questions to the user, present each question separately with clear formatting
5. Avoid large blocks of text - use short paragraphs
6. Speak directly to the user using "you" language
7. Format information about treks in easy-to-scan bullet points when appropriate
8. Keep responses concise yet helpful
              `,
            },
          ],
        },
        safetySettings,
      });

      addMessage("Generating response...", false);
      const result = await model.generateContent({
        contents: [{ role: "user", parts: [{ text: inputText }] }],
      });

      const response = await result.response;
      const responseText = await response.text();
      
      // Format the response with the helper function
      const formattedResponse = formatAIResponse(responseText);
      
      setMessages(prev => prev.filter(msg => msg.text !== "Generating response..."));
      addMessage(formattedResponse, false, true);
    } catch (error) {
      console.error("Error:", error);
      addMessage(`Sorry, I encountered an error. Please try again.`, false);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleChatbot = () => {
    // playBeep();
    setIsOpen(!isOpen);
    if (!isOpen) {
      stopListening();
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    generateContent(input);
  };

  const handleVoiceSubmit = (transcript) => {
    stopListening();
    generateContent(transcript);
  };

  const startListening = () => {
    if (speechRecognitionRef.current) {
      try {
        speechRecognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error("Couldn't start speech recognition:", error);
      }
    }
  };

  const stopListening = () => {
    if (speechRecognitionRef.current) {
      try {
        speechRecognitionRef.current.stop();
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
        }
        setIsListening(false);
      } catch (error) {
        console.error("Couldn't stop speech recognition:", error);
      }
    }
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  const toggleVoice = () => {
    setVoiceEnabled(!voiceEnabled);
    if (isVoiceSpeaking) {
      stopSpeaking();
    }
  };

  const speakText = (text) => {
    if (speechSynthesisRef.current && voiceEnabled) {
      // Stop any ongoing speech
      stopSpeaking();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      
      // Get available voices and select a good one
      const voices = speechSynthesisRef.current.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.lang === 'en-US' && voice.name.includes('Female')
      ) || voices.find(voice => voice.lang === 'en-US') || voices[0];
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      utterance.onstart = () => setIsVoiceSpeaking(true);
      utterance.onend = () => setIsVoiceSpeaking(false);
      utterance.onerror = () => setIsVoiceSpeaking(false);
      
      speechSynthesisRef.current.speak(utterance);
    }
  };

  const stopSpeaking = () => {
    if (speechSynthesisRef.current) {
      speechSynthesisRef.current.cancel();
      setIsVoiceSpeaking(false);
    }
  };

  return (
    <div className={`fixed bottom-4 right-4 z-50 flex flex-col items-end ${isOpen ? 'w-96' : 'w-auto'}`}>
      <button 
        className="w-16 h-16 rounded-full bg-blue-600 shadow-lg flex items-center justify-center hover:bg-blue-700 transition-all duration-300 border border-blue-700"
        onClick={toggleChatbot}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? 
          <X className="h-8 w-8 text-white" /> : 
          <img src={robo} alt="Chatbot" className="h-14 w-14 p-1" />
        }
      </button>
      
      {isOpen && (
        <div className="mt-4 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden w-full animate-fadeIn">
          <div className="p-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Trekking AI Chatbot</h2>
                <p className="text-sm text-blue-100">Your Personal Trekking Assistant</p>
              </div>
              <div className="flex space-x-2">
                <button 
                  onClick={toggleVoice}
                  className={`p-2 rounded-full ${voiceEnabled ? 'bg-blue-500' : 'bg-blue-700'} hover:bg-blue-500 transition-colors`}
                  aria-label={voiceEnabled ? 'Disable voice' : 'Enable voice'}
                >
                  {voiceEnabled ? 
                    <Volume2 className="h-5 w-5 text-white" /> : 
                    <VolumeX className="h-5 w-5 text-white" />
                  }
                </button>
              </div>
            </div>
          </div>
          
          <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg ${
                  message.user 
                    ? 'bg-blue-500 text-white ml-auto max-w-[80%] shadow-md' 
                    : 'bg-white text-gray-800 mr-auto max-w-[80%] shadow-md border border-gray-200'
                } animate-fadeIn`}
              >
                {message.html ? (
                  <div dangerouslySetInnerHTML={{ __html: message.text }} />
                ) : (
                  message.text
                )}
              </div>
            ))}
            {isLoading && (
              <div className="flex space-x-2 p-3 bg-white rounded-lg w-24 shadow-md">
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce delay-100"></div>
                <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce delay-200"></div>
              </div>
            )}
            {isListening && (
              <div className="flex items-center p-2 rounded-lg bg-blue-100 text-blue-800 max-w-[80%] mr-auto animate-pulse">
                <Mic className="h-4 w-4 mr-2" />
                <span className="text-sm">Listening...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          <form onSubmit={handleSubmit} className="p-4 bg-white border-t border-gray-200">
            <div className="flex space-x-2 items-center">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about trekking packages..."
                disabled={isLoading || isListening}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
              />
              <button
                type="button"
                onClick={toggleListening}
                disabled={isLoading}
                className={`p-3 rounded-full ${isListening ? 'bg-red-500' : 'bg-blue-500'} text-white hover:bg-opacity-90 transition-colors`}
                aria-label={isListening ? 'Stop listening' : 'Start listening'}
              >
                {isListening ? 
                  <MicOff className="h-5 w-5" /> : 
                  <Mic className="h-5 w-5" />
                }
              </button>
              <button 
                type="submit" 
                disabled={isLoading || !input.trim()}
                className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
